import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkStatusCard = ({ status, count, jobs, color, icon }) => {
  const getStatusColor = () => {
    const colors = {
      accepted: 'bg-primary/10 text-primary border-primary/20',
      ongoing: 'bg-accent/10 text-accent border-accent/20',
      completed: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[color] || colors?.accepted;
  };

  const getStatusLabel = () => {
    const labels = {
      accepted: 'Geaccepteerd',
      ongoing: 'Lopend',
      completed: 'Voltooid'
    };
    return labels?.[status] || status;
  };

  return (
    <div 
      className="rounded-lg p-4 md:p-6"
      style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${getStatusColor()}`}>
            <Icon name={icon} size={20} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              {getStatusLabel()}
            </h3>
            <p className="caption text-muted-foreground">
              {count} {count === 1 ? 'opdracht' : 'opdrachten'}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {jobs?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="Inbox" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
            <p className="caption text-muted-foreground">Geen {status} opdrachten</p>
          </div>
        ) : (
          jobs?.map((job) => (
            <div key={job?.id} className="bg-muted/50 rounded-lg p-3 md:p-4 border border-border hover:border-primary/30 transition-smooth">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/job-details-and-applications?id=${job?.id}`}
                    className="font-medium text-sm md:text-base text-foreground hover:text-primary transition-smooth line-clamp-1"
                  >
                    {job?.title}
                  </Link>
                  <p className="caption text-muted-foreground line-clamp-1">
                    {job?.client}
                  </p>
                </div>
                {job?.rate && (
                  <div className="flex-shrink-0 text-right">
                    <p className="font-semibold text-sm md:text-base text-foreground whitespace-nowrap">
                      €{job?.rate?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="caption text-muted-foreground whitespace-nowrap">
                      per {job?.rateType === 'hourly' ? 'uur' : 'project'}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 text-xs md:text-sm text-muted-foreground mb-3">
                <div className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  <span className="line-clamp-1">{job?.location}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  <span className="whitespace-nowrap">{job?.startDate}</span>
                </div>
              </div>

              {job?.progress !== undefined && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="caption text-muted-foreground">Voortgang</span>
                    <span className="caption font-medium text-foreground">{job?.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-accent transition-smooth"
                      style={{ width: `${job?.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {job?.hoursWorked !== undefined && (
                <div className="flex items-center justify-between mb-3">
                  <span className="caption text-muted-foreground">Gewerkte uren</span>
                  <span className="caption font-medium text-foreground">{job?.hoursWorked}u</span>
                </div>
              )}

              {job?.affiliate && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-success/5 rounded border border-success/20">
                  <Icon name="Users" size={14} color="var(--color-success)" />
                  <span className="caption text-success">Via: {job?.affiliate}</span>
                </div>
              )}

              <div className="flex gap-2">
                {status === 'accepted' && (
                  <Button variant="outline" size="sm" fullWidth iconName="Play" iconPosition="left">
                    Start Werk
                  </Button>
                )}
                {status === 'ongoing' && (
                  <>
                    <Button variant="outline" size="sm" fullWidth iconName="Clock" iconPosition="left">
                      Tijd Registreren
                    </Button>
                    <Button variant="default" size="sm" fullWidth iconName="CheckCircle" iconPosition="left">
                      Voltooien
                    </Button>
                  </>
                )}
                {status === 'completed' && (
                  <Button variant="outline" size="sm" fullWidth iconName="Eye" iconPosition="left">
                    Details Bekijken
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WorkStatusCard;